import { ProgressBar } from '@tryhackme/thm-ui-components';
import { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { setIsVmReady } from 'src/app/reducers/split-screen.reducer';
import type { RootState } from 'src/app/store';
import { GuacamoleClient } from 'src/common/components/guacamole-client/guacamole-client';
import { GuacamoleLiteClient } from 'src/common/components/guacamole-client/guacamole-lite-client';
import { TaskType } from 'src/common/enums/task';
import { useFeatureIsOn } from 'src/common/growthbook';
import type { RunningInstance } from 'src/common/interfaces/instances';
import { useBootProgress } from '../boot-progress';
import { StyledContent, StyledDescription, StyledIframeWrapper, StyledProgressBarTextVM, StyledTitle } from './split-screen-content.styles';
import type { SplitScreenContentProps } from './split-screen-content.types';
import { useNoVncLiveness } from './use-novnc-liveness';
const focusIFrame = () => {
  const iframe = document.getElementById('remote-window') as HTMLIFrameElement;
  if (iframe && iframe.contentWindow) {
    iframe.contentWindow.focus();
  }
};
const getTypeOfGuacamoleVM = (vm: RunningInstance | undefined): {
  isGuacVm: boolean;
  isGuacLite: boolean;
} => {
  const remoteUrl = vm?.remote?.remoteUrl || '';
  if (remoteUrl.includes('guacaworker.tryhackme')) {
    return {
      isGuacVm: true,
      isGuacLite: false
    };
  }
  if (remoteUrl.startsWith('https://guacamole.cell')) {
    return {
      isGuacVm: true,
      isGuacLite: true
    };
  }
  return {
    isGuacVm: false,
    isGuacLite: false
  };
};
export const SplitScreenContent = ({
  ...props
}: SplitScreenContentProps) => {
  const {
    title,
    url,
    type,
    vm,
    id,
    isVmReady
  } = props.currentTabData;
  const {
    message
  } = useAppSelector((state: RootState) => state.splitScreen);
  const dispatch = useAppDispatch();
  const [guacamoleFailed, setGuacamoleFailed] = useState<boolean>(false);
  const isGuacCommonJsEnabled = useFeatureIsOn('guac-common-js');
  const isNoVNCWebsocketLivenessEnabled = useFeatureIsOn('novnc-websocket-liveness');
  const {
    bootProgress
  } = useBootProgress();
  const handleNovncAvailable = useCallback(() => {
    dispatch(setIsVmReady({
      tabId: id,
      isVmReady: true
    }));
  }, [dispatch, id]);
  useNoVncLiveness({
    remoteUrl: vm?.remote?.remoteUrl,
    skip: !isNoVNCWebsocketLivenessEnabled || vm?.remote?.protocol !== 'vnc',
    isVmReady,
    onAvailable: handleNovncAvailable
  });
  const handleSuccessfulGuacamoleConnection = useCallback(() => {
    dispatch(setIsVmReady({
      tabId: id,
      isVmReady: true
    }));
  }, [dispatch, id]);
  const handleFailedGuacamoleConnection = useCallback(() => {
    setGuacamoleFailed(true);
  }, []);
  const {
    isGuacVm,
    isGuacLite
  } = getTypeOfGuacamoleVM(vm);
  const shouldForceGuacamoleClient = isGuacCommonJsEnabled && isGuacVm && !guacamoleFailed && vm?.remote?.remoteUrl;
  const isGuacamoleLiteLiveClientEnabled = useFeatureIsOn('guacamole-lite-liveness');
  if (isGuacamoleLiteLiveClientEnabled && shouldForceGuacamoleClient && isGuacLite) {
    return <GuacamoleLiteClient
    // biome-ignore lint/style/noNonNullAssertion: remoteUrl is guaranteed to be set on shouldForceGuacamoleClient check
    remoteUrl={vm.remote.remoteUrl!} isVmReady={isVmReady} onConnectionFailed={handleFailedGuacamoleConnection} onSuccessfulConnection={handleSuccessfulGuacamoleConnection} region={vm?.region} />;
  }
  if (!isVmReady && bootProgress && bootProgress < 100) {
    return <StyledContent>
        <StyledTitle>Your machine is initializing...</StyledTitle>
        <br />
        <StyledDescription>{message}</StyledDescription>
        <ProgressBar height={8} title={`${bootProgress}`} value={bootProgress} />
        <StyledProgressBarTextVM>Loading ( {bootProgress}% )</StyledProgressBarTextVM>
      </StyledContent>;
  }
  if (shouldForceGuacamoleClient) {
    return <GuacamoleClient
    // biome-ignore lint/style/noNonNullAssertion: remoteUrl is guaranteed to be set on shouldForceGuacamoleClient check
    remoteUrl={vm.remote.remoteUrl!} onConnectionFailed={() => {
      setGuacamoleFailed(true);
    }} />;
  }
  return <StyledIframeWrapper data-sentry-element="StyledIframeWrapper" data-sentry-component="SplitScreenContent" data-sentry-source-file="split-screen-content.tsx">
      <iframe id="remote-window" width="100%" height="100%" style={type === TaskType.STATIC_SITE || vm?.remote?.protocol?.startsWith('http') ? {
      backgroundColor: 'rgb(255, 255, 255)'
    } : {
      transformOrigin: 'top left',
      transform: 'translate3d(0, 0, 0)',
      backfaceVisibility: 'hidden'
    }} allow="fullscreen; clipboard-read; clipboard-write" title={title} src={url} onMouseOver={focusIFrame} onFocus={focusIFrame} />
    </StyledIframeWrapper>;
};